import React, { FormEvent } from "react";
import { QuickSearchResultItem } from "app/search/interfaces";

import "./style.scss";
import { getSearchPageUrl } from "app/search/urls";
import { useBrowserOnly } from "app/main/hooks";
import { LoadingIndicator } from "app/layout/LoadingIndicator/LoadingIndicator";
import { getUrlForInstrument } from "app/main/urlFormatter";
import { useRouter } from "next/router";
import { SearchTextField } from "app/search/SearchTextField/SearchTextField";
import { DynamicLink } from "app/links/DynamicLink/DynamicLink";
import { SizeObserveMode, SizeObserver } from "app/widgets/SizeObserver";

export interface Props {
    value: string;
    onChange: React.Dispatch<string>;
    results: QuickSearchResultItem[] | null;
    loadingResults?: boolean;
    /** WidgetMode disables the soft css fade in/out of the result list for iframe height calculation */
    widgetMode?: boolean;
    onEnter?: (searchString: string) => void;
}

export interface ResultItemProps {
    name: string;
    isin: string;
    type: string;
}

const typeMap: { [key: string]: string } = {
    Share: "Aktie",
    Fund: "Fonds",
    Commodity: "Rohstoff"
};

const ResultItem = ({ name, isin, type }: ResultItemProps) => {
    return (
        <DynamicLink href={getUrlForInstrument(type, isin, name)} className={type.toLowerCase()}>
            <span className="title">{name}</span>
            <span className="type">{typeMap[type] || type}</span>
        </DynamicLink>
    );
};

function shouldResultListBeVisible(
    isBrowser: boolean,
    widgetMode: boolean,
    hasFocus: boolean,
    wasSubmitted: boolean,
    results: null | QuickSearchResultItem[]
) {
    if (widgetMode) {
        return isBrowser && hasFocus && results && results.length > 0;
    }
    return isBrowser && results && results.length > 0 && !wasSubmitted;
}

export const QuickSearchField = ({
                                     value,
                                     onChange,
                                     onEnter,
                                     results = [],
                                     loadingResults,
                                     widgetMode = false
                                 }: Props) => {
    const classNames = ["quickSearchField"];
    const isBrowser = useBrowserOnly();
    const [hasFocus, setFocused] = React.useState(false);
    const [wasSubmitted, setSubmitted] = React.useState(false);
    const router = useRouter();

    if (results && results.length) {
        classNames.push("hasResults");
    }

    if (loadingResults) {
        classNames.push("loading");
    }

    if (hasFocus && !wasSubmitted) {
        classNames.push("focused");
    }

    const handleSubmit = (e: FormEvent) => {
        if (widgetMode) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        setSubmitted(true);
        if (onEnter) {
            onEnter(value);
            return;
        }
        router.push(getSearchPageUrl(value));
    };

    return (
        <form
            method="get"
            action={getSearchPageUrl()}
            target={widgetMode ? "_top" : undefined}
            onSubmit={handleSubmit}
            className={classNames.join(" ")}
        >
            {loadingResults && <LoadingIndicator small />}
            <SearchTextField
                value={value}
                onChange={onChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 300)}
                setSubmitted={setSubmitted}
                placeholder="Wertpapiersuche: Name, ISIN, WKN"
            />
            {shouldResultListBeVisible(isBrowser, widgetMode, hasFocus, wasSubmitted, results) && (
                <SizeObserver className="results" mode={SizeObserveMode.FLOAT}>
                    <span className="category">TOP-TREFFER</span>

                    {results && results.map(result => <ResultItem {...result} key={result.isin} />)}
                </SizeObserver>
            )}
        </form>
    );
};
